import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  CheckCircle2,
  XCircle,
  User as UserIcon,
} from "lucide-react";
import { getAllLeaves, approveLeave, rejectLeave } from "../../api/leaves/leaveApi";
import { Badge, Button } from "../ui";
import WidgetCard from "./WidgetCard";
import { useToast } from "../../context/ToastContext";

function formatLeaveType(leaveType) {
  if (!leaveType) return "Leave";
  if (typeof leaveType === "object") return leaveType.name || "Leave";
  return String(leaveType);
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr.includes("T") ? dateStr : `${dateStr}T00:00:00`);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

export default function LeaveRequestsWidget({ limit = 5 }) {
  const navigate = useNavigate();

  const [leaves, setLeaves] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const toast = useToast();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllLeaves({});
      const list = Array.isArray(res) ? res : res.data || res.leaves || [];

      setCounts({
        pending: list.filter((l) => l.status === "PENDING").length,
        approved: list.filter((l) => l.status === "APPROVED").length,
        rejected: list.filter((l) => l.status === "REJECTED").length,
      });

      setLeaves(
        list
          .filter((l) => l.status === "PENDING")
          .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
          .slice(0, limit)
      );
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load leave requests");
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApprove = async (id) => {
    setActingId(id);
    try {
      await approveLeave(id, {});
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to approve leave");
    } finally {
      setActingId(null);
    }
  };

  const handleReject = async (id) => {
    setActingId(id);
    try {
      await rejectLeave(id, { approvalNote: "Rejected from dashboard" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to reject leave");
    } finally {
      setActingId(null);
    }
  };

  return (
    <WidgetCard
      title="Leave Requests"
      icon={Calendar}
      loading={loading}
      empty={!loading && leaves.length === 0}
      emptyText="No pending leave requests."
      onViewAll={() => navigate("/leave-management")}
      badge={
        counts.pending > 0 && (
          <Badge variant="warning" className="h-5 min-w-[1.25rem] px-1.5 text-[10px] font-extrabold rounded-full">
            {counts.pending}
          </Badge>
        )
      }
    >
      

      {/* Quick status counts */}
      <div className="mb-4 grid grid-cols-3 gap-2 sm:gap-2.5">
        <div className="rounded-xl bg-amber-50/80 border border-amber-200/80 p-2.5 text-center transition-all hover:bg-amber-100/60 shadow-2xs">
          <p className="text-base sm:text-lg font-extrabold text-amber-900 leading-none tabular-nums">{counts.pending}</p>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-amber-700">Pending</p>
        </div>
        <div className="rounded-xl bg-emerald-50/80 border border-emerald-200/80 p-2.5 text-center transition-all hover:bg-emerald-100/60 shadow-2xs">
          <p className="text-base sm:text-lg font-extrabold text-emerald-900 leading-none tabular-nums">{counts.approved}</p>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700">Approved</p>
        </div>
        <div className="rounded-xl bg-rose-50/80 border border-rose-200/80 p-2.5 text-center transition-all hover:bg-rose-100/60 shadow-2xs">
          <p className="text-base sm:text-lg font-extrabold text-rose-900 leading-none tabular-nums">{counts.rejected}</p>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-rose-700">Rejected</p>
        </div>
      </div>

      {/* Pending requests list */}
      <div className="space-y-2">
        {leaves.map((leave) => (
          <div
            key={leave._id}
            className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-3 rounded-xl border border-slate-200/80 bg-gradient-to-r from-slate-50/60 to-white p-3 transition-all hover:border-indigo-200 hover:shadow-xs"
          >
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-100 text-violet-700 ring-1 ring-violet-500/20 shadow-2xs">
                <UserIcon className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-extrabold text-slate-900 dark:text-neutral-100">
                  {leave.staffId?.name || "Staff"}
                </p>
                <p className="truncate text-[11px] font-medium text-slate-500 dark:text-neutral-400 mt-0.5">
                  <span className="text-violet-700 font-semibold">{formatLeaveType(leave.leaveType)}</span> · {formatDate(leave.startDate)}–
                  {formatDate(leave.endDate)}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between sm:justify-end shrink-0 gap-2 pt-2 border-t border-slate-100 dark:border-neutral-800 sm:border-0 sm:pt-0">
              <Badge variant="secondary" className="text-[10px] px-2 py-0.5 font-extrabold bg-slate-100 dark:bg-neutral-900 text-slate-700 dark:text-neutral-200 border-slate-200 dark:border-neutral-800 shadow-2xs">
                {leave.totalDays}d
              </Badge>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  disabled={actingId === leave._id}
                  onClick={() => handleReject(leave._id)}
                  className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-50 hover:text-rose-700 rounded-lg active:scale-95 transition-transform"
                  aria-label="Reject"
                >
                  <XCircle className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  disabled={actingId === leave._id}
                  onClick={() => handleApprove(leave._id)}
                  className="h-7 w-7 p-0 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700 rounded-lg active:scale-95 transition-transform"
                  aria-label="Approve"
                >
                  <CheckCircle2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </WidgetCard>
  );
}